import { toast } from "react-toastify"
import moment from "moment";
import { capitalize, isEmpty } from "lodash";
import { pdf } from "@react-pdf/renderer";
import { useFormikContext } from "formik";
import { useMemo } from "react";
import AppInputField from "components/shared/FormComponents/AppInputField";
import { generatePath } from "react-router-dom";
import { CHATTER_CLIENT_LIST, HOME_EDUCATION_LIST, PRIVATE_CLIENT_LIST, SCHOOL_CLIENT_DETAIL, SCHOOL_CLIENT_LIST, VIEW_CHATTER_CLIENT, VIEW_HOME_EDUCATION, VIEW_PRIVATE_CLIENT } from "config/paths";
import parse from "html-react-parser";
import { VIEW_CLIENT_CHATTER } from "config/apiPaths";
import { AlertCircle, CheckCircle } from "react-feather";

export const successMessageToast = (msg, options = {}) => {
  toast.success(msg, {
    icon: <CheckCircle size={20} color="rgb(49, 93, 153)" />,
    progressStyle: {
      background: "rgb(49, 93, 153)",
    },
    ...options
  });
}

export const errorMessageToast = (msg, options = {}) => {
  toast.error(msg, {
    // style: {
    // },
    icon: <AlertCircle size={20} color="rgb(49, 93, 153)" />,
    progressStyle: {
      background: "rgb(49, 93, 153)",
    },
    ...options
  });
}

export const promiseToast = async (promise, options = {}) => {
  return toast.promise(
    promise, {
    pending: 'Loading...',
    success: 'Success',
    error: 'Failed',
    ...options
  },
    options
  );
}

export const confirmMsg = async (msg = "Are you sure?") => {
  if (!window.confirm(msg)) {
    return false;
  }

  return true;
}

export function secondsToMinutesAndSeconds(inputSeconds) {
  var minutes = Math.floor(inputSeconds / 60);
  var seconds = inputSeconds % 60;
  return {
    minutes,
    seconds
  };
}

export function userFullName(user) {
  let fullName = user?.firstname;
  if (user?.lastname) {
    fullName += " " + user?.lastname;
  }

  return fullName;
}

export function uniqueString(length = 10) {
  const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    const randomIndex = Math.floor(Math.random() * charset.length);
    result += charset[randomIndex];
  }
  return result;
}

export function getNameInitials(user) {
  if (!user?.firstname || !user?.lastname) return '';

  const firstInitial = user?.firstname[0].toUpperCase();
  const lastInitial = user?.lastname[0].toUpperCase();

  return `${firstInitial}${lastInitial}`;
}


export function dateFormat(date, outputFormat = "DD/MM/YYYY", inputFormat = null) {
  return moment(date, inputFormat).format(outputFormat);
}


export const formatString = (input) => {
  if (!input) {
    return "--"
  }

  return input
    .toLowerCase() // Ensure the string is in lowercase for consistent formatting
    .replace(/_/g, " ") // Replace underscores with spaces
    .split(" ") // Split into words
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1)) // Capitalize the first letter of each word
    .join(" "); // Join the words back into a single string
};

export function filterNullValues(obj) {

}

export const getAge = (date) => {

  const dob = moment(date);
  const today = moment();

  const years = today.diff(dob, 'years');
  dob.add(years, 'years');
  const months = today.diff(dob, 'months');

  return `${years}y, ${months}m`;
};

export function convertToMomentDate(date, inputFormat = "YYYY-MM-DD") {
  if (!date || !moment(date, inputFormat).isValid()) {
    return "--"
  }

  return moment(date, inputFormat).format("DD/MM/YYYY");
}

export const convert12HourTime = (time, format = "HH:mm") => {
  return time ? moment(time, format).format("hh:mm A") : "--"
}

export function dateTimeFormat(date) {
  if (!date || !moment(date).isValid()) {
    return "--"
  }

  return moment(date).format("DD/MM/YYYY hh:mm A");
}

export const replacePlaceholders = (template, values) => {
  if (!template || isEmpty(values)) {
    return template;
  }

  return template.replace(/{(\w+)}/g, (match, placeholder) => {
    console.log("match", match, placeholder)
    // Use the placeholder (like THERAPIST_NAME) to get the value from the `values` object
    return values.hasOwnProperty(placeholder) ? values[placeholder] : match;
  });
};

export const parseJson = (string) => {
  try {
    return JSON.parse(string);
  } catch (err) { return false; };
};

export const generateCustomPath = (path, params) => {
  return path.replace(/:([a-zA-Z0-9_]+)/g, (_, key) => params[key] || "");
};

export const getGuardianOptionLabel = (guardian) => {
  return `${userFullName(guardian)} (${guardian.email})`
}

export function userFullNameReverse(user, separator = ",") {
  let fullName = capitalize(user?.lastname);
  if (user?.firstname) {
    fullName += `${separator ? separator : ""} ${capitalize(user?.firstname)}`;
  }

  return fullName;
}

export const getPdfBlob = async (doc, returnBlob = false, fileName = "intake_report.pdf") => {
  const asPdf = pdf([]);
  asPdf.updateContainer(doc);
  const blob = await asPdf.toBlob();

  if (returnBlob) {
    return blob;
  }

  return new File([blob], fileName, { type: blob.type });
}

export const formatUSD = (amount) => {
  if (isNaN(amount)) return "$0.00";

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

export const getGoalTrackingPercentage = (parameter, goalTracking) => {
  let parameterOccurrence = goalTracking?.filter((tracking) => tracking.tracked === parameter);
  let parameterPercentage = (parameterOccurrence?.length / goalTracking?.length) * 100; // (value / totalValue) * 100;
  return !isNaN(parameterPercentage) ? Math.ceil(parameterPercentage) : 0;
}

export const truncateUrl = (name, maxLength = 20) => {
  if (!name) return "";
  const decodedName = decodeURIComponent(name);
  return decodedName.length > maxLength
    ? decodedName.substring(0, maxLength) + "..."
    : decodedName;
};


export const dobFormat = "MMM DD, YYYY";

export const extractUploadFileName = (file) => {
  if (!file) {
    return
  }
  return file.split('/').pop().split('?')[0]
}

export const isValidValue = (val) => !!val && val !== "null";

export function normalizeHtmlSpaces(html) {
  if (!html) {
    return "";
  }

  // Ensure space before opening inline tags
  html = html?.replace(/([a-zA-Z0-9.,!?])<(strong|em|b|i)>/g, '$1 <$2>');

  // Ensure space after closing inline tags
  html = html?.replace(/<\/(strong|em|b|i)>([a-zA-Z0-9.,!?])/g, '</$1> $2');

  // Collapse multiple spaces to a single space
  html = html?.replace(/\s{2,}/g, ' ');

  return html;
}


export const isHTMLContentEmpty = (html) => {
  if (!html) return true;

  // Remove all tags and whitespace
  const stripped = html.replace(/<[^>]*>/g, '').trim();

  return stripped.length === 0;
};


export const PdfPublishForm = ({ handleSubmit, guardianList, clientType, setShowSubmitModal, publishPdfCheckbox = false }) => {
  const { values, setFieldValue, errors, touched, setFieldTouched, isSubmitting } = useFormikContext()
  const generatePassword = () => {
    const newPass = Math.random().toString(36).slice(-8);
    setFieldValue("emailFormData.pdfPassword", newPass);
  };

  const guardians = useMemo(() => {
    if (!guardianList?.length) return [];

    return guardianList.filter((guardian) => {
      const slug = guardian?.client_guardian?.client_role?.slug;
      if (slug !== (clientType === "home_education" ? "parent" : "teacher")) {
        return false;
      }

      return true;
    })
  }, [guardianList]);

  return (
    // <div style={{ width: "379px", padding: "20px", backgroundColor: "white", position: "absolute", right: "80px", top: "87px", boxShadow: "rgba(100, 100, 111, 0.2) 0px 7px 29px 0px" }}>
    <div
      className="proxima-regular"
      style={{
        // margin: "20px auto",
        // padding: "20px",
        // border: "1px solid #ddd",
        borderRadius: "6px",
        background: "#fff",
        position: "relative"
      }}
    >
      {/* Header */}
      {/* <Row style={{ marginBottom: "10px" }}>
          <Col style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <h6
              style={{
                margin: 0,
                width: "100%",
                fontSize: "14px",
                fontWeight: "800",
                textTransform: "uppercase",
                borderBottom: "1px solid #315D99",
                color: "#315D99",
              }}
            >
              Submit & Export
            </h6>
          </Col>
        </Row> */}

      {/* Email To */}
      <div style={{ borderBottom: "1px solid #ddd", paddingBottom: "15px" }}>
        {/* <p style={{
            fontSize: "14px", marginBottom: "10px", textTransform: "uppercase",
            borderBottom: "1px solid #315D99",
            fontWeight: "600",
            color: "#315D99",
          }}>Email To</p> */}
        {guardians?.length > 0 ?
          guardians
            .map((guardian, index) => {
              const slug = guardian?.client_guardian?.client_role?.slug;
              if (slug !== (clientType === "school" ? "teacher" : "parent")) {
                return null;
              }
              if ([0, 1].includes(index)) {
                return (
                  <div className="d-flex gap-2 mb-2">
                    <input
                      type="checkbox"
                      className="form-check"
                      style={{ accentColor: "#b46c9f", height: 20, width: 20, }}
                      // disabled={true}
                      onChange={(e) => {
                        const isChecked = e.target.checked;
                        let role = ""
                        if (slug == 'teacher') {
                          role = index === 0 ? "teacher" : "coordinator";
                        }
                        else {
                          role = index === 0 ? "parent1" : "parent2";
                        }
                        const guardianData = { id: guardian?.client_guardian?.id, role, firstname: guardian?.client_guardian?.firstname, lastname: guardian?.client_guardian?.lastname, email: guardian?.client_guardian?.email };

                        const currentList = values.emailFormData?.recipientList || [];

                        let updatedList;
                        if (isChecked) {
                          updatedList = [...currentList, guardianData];
                        } else {
                          updatedList = currentList.filter((item) => item.id !== guardian?.client_guardian?.id);
                        }

                        setFieldValue('emailFormData.recipientList', updatedList);
                      }}
                      checked={values?.emailFormData?.recipientList?.some((item) => item?.id === guardian?.client_guardian?.id)}
                    />
                    <div className="proxima-regular" style={{ fontSize: "14px", color: "#000" }}>
                      <strong style={{ color: "#000" }}>{clientType == "school" ? index == 0 ? "Teacher" : "Coordinator" : `Parent ${index + 1}`}:</strong>
                      <p className="mb-0">
                        {userFullName(guardian?.client_guardian)}
                      </p>
                      <p className="mb-0">
                        {guardian?.client_guardian?.email}
                      </p>
                    </div>
                  </div>
                );
              }
              else {
                return
              }
            }) : (
            <div className={`d-flex align-items-center justify-content-center w-100 guardian-not-assigned-wrapper py-2`}>
              <p className="m-0">No teacher is currently assigned to this client.</p>
            </div>
          )}


        {/* Password */}
        {
          guardians?.length > 0 &&
          < div className="proxima-regular" style={{ marginTop: "15px", display: "flex", alignItems: "center", gap: "5px", maxWidth: "400px" }}>
            <label style={{ fontSize: "13px", whiteSpace: "nowrap", fontWeight: "bold" }}>Add a Password:</label>
            <div className="w-100">
              <AppInputField style={{ width: "100%", padding: "8px", border: "1px solid #ccc", fontFamily: "Proxima Nova" }} name={"emailFormData.pdfPassword"} />
              <div className="error">
                {(touched?.emailFormData?.pdfPassword && !values?.emailFormData?.pdfPassword && values?.emailFormData?.recipientList?.length > 0) && "Password is required"}
              </div>
            </div>
          </div>
        }
      </div>

      {/* Publish Report */}
      {
        publishPdfCheckbox &&
        <div style={{ marginTop: "15px", paddingBottom: "15px" }}>
          <div className="section-title d-flex align-items-center justify-content-between" style={{ borderColor: "#315D99" }}>
            <h6 className="mb-0 section-title border-bottom-0" style={{ color: "#315D99", padding: 0 }}>{"PUBLISH REPORT TO"}</h6>
          </div>
          <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "14px" }}>
            <input
              type="checkbox"
              className="form-check"
              style={{ accentColor: "#b46c9f", height: 20, width: 20, borderColor: "#999" }}
              // disabled={previewMode}
              onChange={(e) => {
                const isChecked = e.target.checked;
                if (isChecked) {
                  setFieldValue('emailFormData.publishReport', "yes")
                } else {
                  setFieldValue('emailFormData.publishReport', "no")
                }
              }}
              checked={values?.emailFormData?.publishReport == 'yes'}
            />
            {clientType === "school" ? "Teacher" : "Parent"} Dashboard
          </label>
        </div>
      }

      {/* Done Button */}
      <div style={{ display: "flex", justifyContent: "end", textAlign: "right", marginTop: "15px" }}>
        <button disabled={isSubmitting} className="d-flex align-items-center gap-2 justify-content-center discharge-screen" type='button' onClick={() => {
          if (isEmpty(errors) && (values?.emailFormData?.pdfPassword || !values?.emailFormData?.recipientList?.length)) {
            setFieldValue('saveForLater', false); handleSubmit()
          }
          else {
            setFieldTouched("emailFormData.pdfPassword", true);
            errorMessageToast("There are errors in the form. Please check your entries and correct them.")
          }
        }} style={{ background: "#0d3e7d" }}>
          Done
        </button>
      </div>
    </div >
    // </div>
  )
}


export const PdfPeerForm = ({ handleSubmit, guardianList, clientType, setShowSubmitModal, publishPdfCheckbox = false }) => {
  const { values, setFieldValue, errors, touched, setFieldTouched, isSubmitting } = useFormikContext()

  const generatePassword = () => {
    const newPass = Math.random().toString(36).slice(-8);
    setFieldValue("emailFormData.pdfPassword", newPass);
  };

  const guardians = useMemo(() => {
    if (!guardianList?.length) return [];

    return guardianList.filter((guardian) => {
      const slug = guardian?.client_guardian?.client_role?.slug;
      if (slug !== (clientType === "peer" ? "peer" : "")) {
        return false;
      }

      return true;
    })
  }, [guardianList]);
  console.log('errors-=', errors);

  return (
    // <div style={{ width: "379px", padding: "20px", backgroundColor: "white", position: "absolute", right: "80px", top: "87px", boxShadow: "rgba(100, 100, 111, 0.2) 0px 7px 29px 0px" }}>
    <div
      className="proxima-regular"
      style={{
        // margin: "20px auto",
        // padding: "20px",
        // border: "1px solid #ddd",
        borderRadius: "6px",
        background: "#fff",
        position: "relative"
      }}
    >
      {/* Header */}
      {/* <Row style={{ marginBottom: "10px" }}>
          <Col style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <h6
              style={{
                margin: 0,
                width: "100%",
                fontSize: "14px",
                fontWeight: "800",
                textTransform: "uppercase",
                borderBottom: "1px solid #315D99",
                color: "#315D99",
              }}
            >
              Submit & Export
            </h6>
          </Col>
        </Row> */}

      {/* Email To */}
      <div style={{ paddingBottom: "15px" }}>
        {/* <p style={{
            fontSize: "14px", marginBottom: "10px", textTransform: "uppercase",
            borderBottom: "1px solid #315D99",
            fontWeight: "600",
            color: "#315D99",
          }}>Email To</p> */}
        {guardians?.length > 0 ?
          guardians
            .map((guardian, index) => {
              const slug = guardian?.client_guardian?.client_role?.slug;
              if (slug !== (clientType === "peer" ? "peer" : "")) {
                return null;
              }
              if ([0, 1].includes(index)) {
                return (
                  <div className="d-flex gap-2 mb-2">
                    <input
                      type="checkbox"
                      className="form-check"
                      style={{ accentColor: "#b46c9f", height: 20, width: 20, }}
                      // disabled={true}
                      onChange={(e) => {
                        const isChecked = e.target.checked;
                        let role = ""
                        if (slug == 'teacher') {
                          role = index === 0 ? "teacher" : "coordinator";
                        }
                        else {
                          role = index === 0 ? "parent1" : "parent2";
                        }
                        const guardianData = { id: guardian?.client_guardian?.id, role, firstname: guardian?.client_guardian?.firstname, lastname: guardian?.client_guardian?.lastname, email: guardian?.client_guardian?.email };

                        const currentList = values.emailFormData?.recipientList || [];

                        let updatedList;
                        if (isChecked) {
                          updatedList = [...currentList, guardianData];
                        } else {
                          updatedList = currentList.filter((item) => item.id !== guardian?.client_guardian?.id);
                        }

                        setFieldValue('emailFormData.recipientList', updatedList);
                      }}
                      checked={values?.emailFormData?.recipientList?.some((item) => item?.id === guardian?.client_guardian?.id)}
                    />
                    <div className="proxima-regular" style={{ fontSize: "14px", color: "#000" }}>
                      <strong style={{ color: "#000" }}>{clientType === "peer" ? "Peer" : ""}:</strong>
                      <p className="mb-0">
                        {userFullName(guardian?.client_guardian)}
                      </p>
                      <p className="mb-0">
                        {guardian?.client_guardian?.email}
                      </p>
                    </div>
                  </div>
                );
              }
              else {
                return
              }
            }) : (
            <div className={`d-flex align-items-center justify-content-center w-100 guardian-not-assigned-wrapper py-2`}>
              <p className="m-0">No teacher is currently assigned to this client.</p>
            </div>
          )}


        {/* Password */}
        {/* {
          guardians?.length > 0 &&
          < div className="proxima-regular" style={{ marginTop: "15px", display: "flex", alignItems: "center", gap: "5px", maxWidth: "400px" }}>
            <label style={{ fontSize: "13px", whiteSpace: "nowrap", fontWeight: "bold" }}>Add a Password:</label>
            <div className="w-100">
              <AppInputField style={{ width: "100%", padding: "8px", border: "1px solid #ccc", fontFamily: "Proxima Nova" }} name={"emailFormData.pdfPassword"} />
              <div className="error">
                {(touched?.emailFormData?.pdfPassword && !values?.emailFormData?.pdfPassword && values?.emailFormData?.recipientList?.length > 0) && "Password is required"}
              </div>
            </div>
          </div>
        }
      </div> */}

        {/* Publish Report */}
        {
          publishPdfCheckbox &&
          <div style={{ marginTop: "15px", paddingBottom: "15px" }}>
            <div className="section-title d-flex align-items-center justify-content-between" style={{ borderColor: "#315D99" }}>
              <h6 className="mb-0 section-title border-bottom-0" style={{ color: "#315D99", padding: 0 }}>{"PUBLISH REPORT TO"}</h6>
            </div>
            <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "14px" }}>
              <input
                type="checkbox"
                className="form-check"
                style={{ accentColor: "#b46c9f", height: 20, width: 20, borderColor: "#999" }}
                // disabled={previewMode}
                onChange={(e) => {
                  const isChecked = e.target.checked;
                  if (isChecked) {
                    setFieldValue('emailFormData.publishReport', "yes")
                  } else {
                    setFieldValue('emailFormData.publishReport', "no")
                  }
                }}
                checked={values?.emailFormData?.publishReport == 'yes'}
              />
              {clientType === "school" ? "Teacher" : "Parent"} Dashboard
            </label>
          </div>
        }

        {/* Done Button */}
        <div style={{ display: "flex", justifyContent: "end", textAlign: "right", marginTop: "15px" }}>
          <button disabled={isSubmitting} className="d-flex align-items-center gap-2 justify-content-center discharge-screen" type='button' onClick={() => {


            // setFieldValue('save_for_later', false); handleSubmit()
            if (isEmpty(errors)) {
              setFieldValue('save_for_later', false); handleSubmit()
            }
            else {
              setFieldTouched("emailFormData.pdfPassword", true);
              errorMessageToast("There are errors in the form. Please check your entries and correct them.")
            }
          }} style={{ background: "#0d3e7d" }}>
            Done
          </button>
        </div>
      </div >
    </div>
  )
}

export const truncateString = (text, limit = 30) => {
  if (!text) return "";
  return text.length > limit ? text.slice(0, limit) + "..." : text;
};

export function dateFormatLocal(date, outputFormat = "DD/MM/YYYY") {
  if (!date || !moment(date).isValid()) {
    return "--"
  }

  return moment(date).format(outputFormat);
}

export const getClientDetailPathByType = (clientType) => {
  switch (clientType) {
    case "school":
      return SCHOOL_CLIENT_DETAIL;
    case "chatter":
      return VIEW_CHATTER_CLIENT;
    case "home-education":
      return VIEW_HOME_EDUCATION;
    case "private":
      return VIEW_PRIVATE_CLIENT;
  }
}

export const getClientListPathByType = (clientType) => {
  switch (clientType) {
    case "school":
      return SCHOOL_CLIENT_LIST;
    case "chatter":
      return CHATTER_CLIENT_LIST
    case "home-education":
      return HOME_EDUCATION_LIST;
    case "private":
      return PRIVATE_CLIENT_LIST;
  }
}

export const getClientBreadCrumbs = (clientId, clientType, clientDetails, currentPage) => {
  return [
    { label: `${formatString(clientType)} Client`, path: getClientListPathByType(clientType) },
    { label: userFullName(clientDetails) || "Client Details", path: generatePath(getClientDetailPathByType(clientType), { id: clientId }) },
    { label: currentPage },
  ];
}

export const NoRecordMessage = ({ msg = 'No Record Found', className = "", ...props }) => {
  return <div {...props} className={`py-2 d-flex align-items-center justify-content-center w-100 ${"guardian-not-assigned-wrapper"} ${className}`}>
    {msg}
  </div>
}

export const parseHtmlHelper = (html) => {
  return parse(html || "");
}

export const notificationCategoryLabel = (category) => category.replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
